import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { Action } from 'history';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BlockerFunction, useBlocker } from 'react-router-dom';
import { useModal } from 'utils/hooks/useModal';

export const DirtyFormGuard = (): JSX.Element => {
  const { t } = useTranslation();
  const { modalProps, openModal, closeModal } = useModal({ dismissable: false });

  const {
    formState: { isDirty, dirtyFields, isSubmitting, isSubmitSuccessful },
  } = useFormContext();

  /**
   * @NOTE: RHF dirty field checking doesn't work all that well, so we have to
   * also check the length of `dirtyFields`
   * https://github.com/react-hook-form/react-hook-form/issues/3213
   */
  const isActuallyDirty = isDirty && Object.keys(dirtyFields).length > 0;

  const shouldBlock = isActuallyDirty && !isSubmitting && !isSubmitSuccessful;

  const blocker: BlockerFunction = useCallback(
    ({ historyAction }) => {
      if (historyAction === Action.Replace) {
        return false;
      } else if (shouldBlock) {
        openModal();
        return true;
      }

      return false;
    },
    [openModal, shouldBlock],
  );

  const { proceed, reset, state } = useBlocker(blocker);

  return (
    <Modal {...modalProps} open={state === 'blocked'}>
      <Modal.DefaultTitle>{t('form.dirtyModal.title')}</Modal.DefaultTitle>
      <Modal.DefaultDescription>{t('form.dirtyModal.description')}</Modal.DefaultDescription>

      <div className="flex flex-col md:flex-row gap-12 mt-24 md:mt-32">
        <Button className="sm-d:w-full md:min-w-[200px]" onClick={proceed}>
          {t('form.dirtyModal.confirmButtonText')}
        </Button>

        <Button
          className="sm-d:w-full md:min-w-[160px]"
          onClick={() => {
            closeModal();
            reset?.();
          }}
        >
          {t('form.dirtyModal.denyButtonText')}
        </Button>
      </div>
    </Modal>
  );
};
